import React, { useEffect, useState } from "react";
import { getState, subscribe, resetProgress, categoryPercent, sectionMastery } from "@/lib/progress";
import { CATEGORIES } from "@/lib/content";
import { ProgressBar } from "@/components/Bits";
import { ProfileForm, AvatarImg } from "@/components/Profile";
import AccountCard from "@/components/Account";

export default function Parent() {
  const [s, setS] = useState(getState());
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  useEffect(() => subscribe(setS), []);

  const weekly = s.weeklyActivity || {};
  const totalWeekly = Object.values(weekly).reduce((a, b) => a + b, 0);
  const weakest = CATEGORIES.reduce((acc, c) => { const p = sectionMastery(c.id); return p < acc.p ? { id: c.id, title: c.title, p } : acc; }, { id: null, title: null, p: 101 });

  return (
    <div className="ll-bg min-h-screen pb-28 md:pb-8">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
        <div className="pt-4">
          <div className="text-purple-500 uppercase tracking-widest text-xs font-bold">Parent Hub</div>
          <h1 className="font-display font-bold text-4xl text-purple-900 mt-1">Learning insights</h1>
          <p className="text-purple-700/70 font-semibold mt-1">A friendly overview of your little one's progress.</p>
        </div>

        <div className="mt-6"><AccountCard /></div>

        <div className="ll-card p-5 mt-6 flex flex-wrap items-center gap-4" data-testid="profile-card">
          <AvatarImg avatar={s.profile?.avatar} className="w-20 h-20" />
          <div className="flex-1">
            <div className="text-purple-500 uppercase tracking-widest text-xs font-bold">Child profile</div>
            <div className="font-display font-bold text-2xl text-purple-900" data-testid="profile-name">{s.profile?.name || "Not set"}</div>
          </div>
          <button data-testid="edit-profile-button" onClick={() => setEditing(e => !e)} className="ll-btn ll-btn-secondary">{editing ? "Close" : "Edit profile"}</button>
          {editing && <div className="w-full"><ProfileForm compact onSaved={() => setEditing(false)} /></div>}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <StatCard title="Stars earned" value={s.stars} icon="⭐" testId="parent-stars" />
          <StatCard title="Activities" value={s.activitiesCompleted} icon="🎯" testId="parent-activities" />
          <StatCard title="Streak" value={`${s.streak} days`} icon="🔥" testId="parent-streak" />
          <StatCard title="Correct this week" value={totalWeekly} icon="📅" testId="parent-weekly" />
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <div className="ll-card p-6">
            <h2 className="font-display font-bold text-2xl text-purple-900">This week</h2>
            <div className="mt-4 space-y-3">
              {CATEGORIES.map(c => (
                <div key={c.id} className="flex items-center gap-3">
                  <div className="text-2xl w-8">{c.emoji}</div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between"><div className="font-bold text-purple-900">{c.short}</div><div className="text-sm text-purple-500 font-bold">{weekly[c.id] || 0} correct</div></div>
                    <ProgressBar percent={Math.min(100, (weekly[c.id] || 0) * 5)} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="ll-card p-6">
            <h2 className="font-display font-bold text-2xl text-purple-900">Skills mastery</h2>
            <div className="mt-4 space-y-3">
              {CATEGORIES.map(c => (
                <div key={c.id}>
                  <div className="flex items-center justify-between mb-1"><div className="font-bold text-purple-900">{c.short}</div><div className="text-sm text-purple-500 font-bold">{sectionMastery(c.id)}% explored · {categoryPercent(c.id)}% accuracy</div></div>
                  <ProgressBar percent={sectionMastery(c.id)} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="ll-card p-6 mt-6 bg-gradient-to-br from-violet-50 to-purple-50 border-purple-200">
          <div className="flex items-start gap-4">
            <div className="text-4xl">💡</div>
            <div>
              <h3 className="font-display font-bold text-xl text-purple-900">Recommendation</h3>
              <p className="text-purple-700 mt-1">{weakest.id ? <>Try more <b>{weakest.title}</b> activities this week to build mastery.</> : "Great balance across all topics! Keep exploring."}</p>
            </div>
          </div>
        </div>

        <div className="mt-6">
          {!confirming ? (
            <button data-testid="reset-progress-button" onClick={() => setConfirming(true)} className="ll-btn ll-btn-secondary">Reset progress</button>
          ) : (
            <div className="ll-card p-5 bg-pink-50 border-pink-200 flex flex-wrap items-center gap-3" data-testid="reset-confirm">
              <div className="font-bold text-purple-900 flex-1">Reset all learning progress? Stars, badges and streaks will be cleared. The profile is kept.</div>
              <button data-testid="reset-confirm-button" onClick={() => { resetProgress(); setConfirming(false); }} className="ll-btn bg-pink-500 text-white">Yes, reset</button>
              <button data-testid="reset-cancel-button" onClick={() => setConfirming(false)} className="ll-btn ll-btn-secondary">Cancel</button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function StatCard({ title, value, icon, testId }) {
  return (
    <div className="ll-card p-5 text-center" data-testid={testId}>
      <div className="text-3xl">{icon}</div>
      <div className="font-display text-3xl font-bold text-purple-900 mt-1">{value}</div>
      <div className="text-sm font-bold text-purple-600">{title}</div>
    </div>
  );
}
